import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Download, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { downloadFile, safeName } from "@/lib/download";
import SignedCopyModal from "@/components/agreements/SignedCopyModal";

// Searchable signed-records table (member, date range, agreement type).
export default function SignedRecordsTable({ endpoint, types = [], testid = "signed-records" }) {
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [view, setView] = useState(null);
  const { data: rows = [], isLoading } = useQuery({
    queryKey: [testid, endpoint, q, type, from, to],
    queryFn: () => api.get(endpoint, { params: { q, agreement_type: type, date_from: from, date_to: to } }).then((r) => r.data),
  });
  const pdf = async (r) => {
    try { await downloadFile(`/me/signatures/${r.id}/pdf`, `${safeName(r.agreement_title)}_${safeName(r.name)}.pdf`); }
    catch (e) { toast.error(formatApiError(e.message)); }
  };
  return (
    <div data-testid={testid}>
      <div className="grid sm:grid-cols-[1fr_auto_auto_auto] gap-2 mb-4">
        <label className="flex items-center gap-2 eyw-input !py-0">
          <Search size={15} className="text-[#8FA89B]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search member, email or agreement" data-testid={`${testid}-search`} className="w-full py-2.5 outline-none bg-transparent text-sm" />
        </label>
        {types.length > 0 && (
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid={`${testid}-type`} className="eyw-input text-sm">
            <option value="">All types</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From date" data-testid={`${testid}-from`} className="eyw-input text-sm" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To date" data-testid={`${testid}-to`} className="eyw-input text-sm" />
      </div>
      <p className="text-xs text-[#8FA89B] mb-2" data-testid={`${testid}-count`}>{isLoading ? "Loading…" : `${rows.length} signed record${rows.length === 1 ? "" : "s"}`}</p>
      <ul className="divide-y divide-[#E5E0D8] rounded-2xl border border-[#E5E0D8] bg-white">
        {rows.map((r) => (
          <li key={r.id} className="p-3.5 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`${testid}-row-${r.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-[#1A242B]"><b>{r.name || r.user_email || "Guest"}</b> <span className="text-[#8FA89B]">{r.user_email}</span></p>
              <p className="text-xs text-[#4A5D6B]">
                {r.agreement_title} · v{r.agreement_version}{r.agreement_type && ` · ${r.agreement_type}`} · {String(r.signed_at).slice(0, 16).replace("T", " ")}
                {r.booking && ` · ${r.booking.service_title} ${r.booking.date}`}
              </p>
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => setView(r)} data-testid={`${testid}-view-${r.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Eye size={13} /> View</button>
              <button onClick={() => pdf(r)} data-testid={`${testid}-pdf-${r.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><Download size={13} /> PDF</button>
            </div>
          </li>
        ))}
      </ul>
      {view && <SignedCopyModal sigId={view.id} onClose={() => setView(null)} />}
    </div>
  );
}
